"use client";

import translations from "@/lib/translations.json";
import {
  Bell,
  CalendarClock,
  CheckCircle2,
  Clock,
  FileCheck,
  Landmark,
  Receipt,
  TrendingDown,
  XCircle,
} from "lucide-react";
import { useWebsiteLang } from "../context/website-lang-context";

const alertIcons = [
  FileCheck,
  Receipt,
  Landmark,
  CalendarClock,
  TrendingDown,
  CalendarClock,
];
const typeKeys = [
  "info",
  "warning",
  "warning",
  "error",
  "error",
  "warning",
] as const;

const typeConfig = {
  info: {
    bg: "bg-blue-500/10",
    border: "border-blue-500/20",
    icon: "text-blue-400",
  },
  warning: {
    bg: "bg-amber-500/10",
    border: "border-amber-500/20",
    icon: "text-amber-400",
  },
  error: {
    bg: "bg-red-500/10",
    border: "border-red-500/20",
    icon: "text-red-400",
  },
} as const;

export default function Automation() {
  const { lang } = useWebsiteLang();
  const t = translations[lang].website.automation;

  return (
    <section className="relative overflow-hidden py-20 sm:py-28">
      <div className="absolute inset-0 bg-gradient-to-b from-navy-950 via-navy-900/30 to-navy-950" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/20 px-3 py-1.5 text-xs font-medium text-amber-400 glass">
              <Bell size={14} />
              {t.badge}
            </div>
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
              {t.titlePrefix}{" "}
              <span className="text-gradient-amber">{t.titleHighlight}</span>
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-white/50">
              {t.description}
            </p>
            <div className="grid grid-cols-3 gap-4 pt-4">
              {t.stats.map((item) => {
                const iconMap = {
                  auto: CheckCircle2,
                  realtime: Clock,
                  action: XCircle,
                } as const;
                const colorMap = {
                  auto: "text-teal-400",
                  realtime: "text-amber-400",
                  action: "text-red-400",
                } as const;
                const Icon =
                  iconMap[item.key as keyof typeof iconMap] ?? CheckCircle2;

                return (
                  <div
                    key={item.label}
                    className="flex flex-col items-center gap-2 rounded-xl glass p-3"
                  >
                    <Icon
                      size={20}
                      className={colorMap[item.key as keyof typeof colorMap]}
                    />
                    <span className="text-center text-xs text-white/50">
                      {item.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="relative">
            <div className="glass-strong rounded-2xl border border-white/10 p-5 shadow-xl sm:p-6">
              <div className="mb-5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell size={16} className="text-amber-400" />
                  <span className="text-sm font-semibold">{t.panelTitle}</span>
                </div>
                <span className="rounded-full bg-red-500/20 px-2 py-1 text-[10px] font-medium text-red-400">
                  {t.panelCount}
                </span>
              </div>
              <div className="space-y-2">
                {t.alerts.map((alert, index) => {
                  const config = typeConfig[typeKeys[index]];
                  const Icon = alertIcons[index] ?? FileCheck;
                  return (
                    <div
                      key={alert.text}
                      className={`flex items-start gap-3 rounded-xl border p-3 transition-all duration-200 ${config.bg} ${config.border}`}
                    >
                      <Icon
                        size={16}
                        className={`${config.icon} mt-0.5 shrink-0`}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-white/70">{alert.text}</p>
                        <p className="mt-1 text-[10px] text-white/30">
                          {alert.time}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 flex items-center justify-between border-t border-white/5 pt-3">
                <span className="text-[10px] text-white/30">
                  {t.panelFooter}
                </span>
                <span className="cursor-pointer text-[10px] font-medium text-amber-400 hover:text-amber-300">
                  {t.viewAll}
                </span>
              </div>
            </div>
            <div className="absolute -bottom-6 left-1/2 h-12 w-3/4 -translate-x-1/2 rounded-full bg-amber-500/5 blur-2xl" />
          </div>
        </div>
      </div>
    </section>
  );
}
